import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const [plans, setPlans] = useState([]);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.plans)); }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-6 py-10 md:px-10">
        <div className="mb-8">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Workspace</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">Settings</h1>
        </div>

        <Tabs value={tab} onValueChange={(t) => setParams({ tab: t }, { replace: true })} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="settings-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="bot" data-testid="settings-tab-bot">Bot</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6"><ProfileTab user={user} refresh={refreshUser} /></TabsContent>
          <TabsContent value="calendar" className="mt-6"><CalendarTab /></TabsContent>
          <TabsContent value="bot" className="mt-6"><BotTab user={user} refresh={refreshUser} /></TabsContent>
          <TabsContent value="notifications" className="mt-6"><NotificationsTab user={user} refresh={refreshUser} /></TabsContent>
          <TabsContent value="billing" className="mt-6"><BillingTab user={user} plans={plans} refresh={refreshUser} /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function ProfileTab({ user, refresh }) {
  const [name, setName] = useState(user?.name || "");
  const [tz, setTz] = useState(user?.timezone || "Asia/Kolkata");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/profile", { name, timezone: tz });
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="rounded-2xl border border-border bg-card p-6 space-y-5 max-w-xl">
      <div className="space-y-2">
        <Label>Email</Label>
        <Input value={user?.email || ""} disabled />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Full name</Label>
        <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
      </div>
      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select value={tz} onValueChange={setTz}>
          <SelectTrigger data-testid="profile-timezone-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
            <SelectItem value="America/New_York">America/New_York (ET)</SelectItem>
            <SelectItem value="America/Los_Angeles">America/Los_Angeles (PT)</SelectItem>
            <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
            <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
            <SelectItem value="Asia/Dubai">Asia/Dubai</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={saving} data-testid="save-profile">{saving ? "Saving…" : "Save changes"}</Button>
    </form>
  );
}

function CalendarTab() {
  const [params, setParams] = useSearchParams();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [excludedKw, setExcludedKw] = useState("");
  const [excludedDom, setExcludedDom] = useState("");
  const [confirmDisc, setConfirmDisc] = useState(false);
  const { user, refreshUser } = useAuth();

  const load = async () => {
    try {
      const r = await api.get("/calendar/status");
      setConn(r.data.connection);
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Toast feedback for OAuth callback redirects
  useEffect(() => {
    if (params.get("calendar_connected")) {
      toast.success("Calendar connected!");
      const next = new URLSearchParams(params);
      next.delete("calendar_connected");
      setParams(next, { replace: true });
      load();
    } else if (params.get("calendar_error")) {
      toast.error(`Calendar connection failed (${params.get("calendar_error")})`);
      const next = new URLSearchParams(params);
      next.delete("calendar_error");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!user) return;
    setPrefs({
      auto_record_default: user.auto_record_default ?? true,
      auto_record_external_only: user.auto_record_external_only ?? false,
      minimum_meeting_duration_minutes: user.minimum_meeting_duration_minutes ?? 5,
      notify_before_recording: user.notify_before_recording ?? false,
      notify_before_minutes: user.notify_before_minutes ?? 5,
      bot_join_buffer_seconds: user.bot_join_buffer_seconds ?? 60,
    });
    setExcludedKw((user.excluded_keywords || []).join(", "));
    setExcludedDom((user.excluded_email_domains || []).join(", "));
  }, [user]);

  const connect = async (provider) => {
    setBusy(true);
    try {
      const r = await api.post("/calendar/connect", { provider });
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start calendar connection");
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/calendar/sync");
      const { upserted = 0, from_recall = 0, skipped_no_url = 0 } = r.data || {};
      if (upserted > 0) {
        toast.success(`Synced ${upserted} meeting${upserted === 1 ? "" : "s"}`);
      } else if (from_recall === 0) {
        toast.message("Recall returned 0 events", {
          description: "Either your calendar is empty for the next 30 days, or Recall hasn't finished its initial sync yet (can take a few minutes after connecting). Try again shortly.",
        });
      } else {
        toast.message(`Recall returned ${from_recall} event(s) but none had a video link`, {
          description: `${skipped_no_url} event(s) were skipped (no Zoom/Meet/Teams URL in title, description, or location).`,
        });
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/calendar/disconnect");
      toast.success("Calendar disconnected");
      setConfirmDisc(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const savePrefs = async () => {
    if (!prefs) return;
    setSavingPrefs(true);
    try {
      await api.patch("/profile", {
        ...prefs,
        excluded_keywords: excludedKw.split(",").map((s) => s.trim()).filter(Boolean),
        excluded_email_domains: excludedDom.split(",").map((s) => s.trim().replace(/^@/, "")).filter(Boolean),
      });
      await refreshUser();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSavingPrefs(false); }
  };

  if (loading) {
    return <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading calendar status…</div>;
  }

  return (
    <div className="space-y-6 max-w-xl">
      {!conn ? (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Calendar integration</div>
          <h3 className="mt-2 font-display text-xl font-medium">Connect your calendar</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Let Noteology automatically join your meetings without pasting URLs.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => connect("google_calendar")}
              disabled={busy}
              data-testid="connect-google-cal"
            >
              <span className="mr-2">G</span> Connect Google Calendar
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => connect("microsoft_outlook")}
              disabled={busy}
              data-testid="connect-ms-cal"
            >
              <span className="mr-2">O</span> Connect Outlook
            </Button>
          </div>
          <div className="mt-6 space-y-1 text-xs text-muted-foreground">
            <div>✓ We see meeting titles, times, and attendees</div>
            <div>✓ Meeting content is recorded only when bot joins</div>
            <div>✗ No personal calendar data outside scheduled meetings</div>
            <div className="mt-3">You can disconnect at any time.</div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="calendar-connected-card">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500"></span>
            Calendar connected
          </div>
          <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-muted-foreground">Account</div>
            <div className="font-medium">{conn.calendar_email}</div>
            <div className="text-muted-foreground">Provider</div>
            <div className="capitalize">{conn.provider?.replace("_", " ")}</div>
            <div className="text-muted-foreground">Connected</div>
            <div>{conn.connected_at ? new Date(conn.connected_at).toLocaleDateString() : "—"}</div>
            <div className="text-muted-foreground">Last sync</div>
            <div>{conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : "—"}</div>
          </div>
          <div className="mt-6 flex gap-3">
            <Button variant="outline" onClick={syncNow} disabled={busy} data-testid="calendar-sync-now">
              {busy ? "Working…" : "Sync now"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDisc(true)} disabled={busy} data-testid="calendar-disconnect">
              Disconnect
            </Button>
          </div>
          {confirmDisc && (
            <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
              <div className="text-sm font-medium">Disconnect this calendar?</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Scheduled bot deployments will be cancelled. Past meetings remain in your library.
              </p>
              <div className="mt-3 flex gap-3">
                <Button size="sm" variant="destructive" onClick={disconnect} data-testid="confirm-disconnect">
                  Yes, disconnect
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDisc(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {prefs && (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Auto-record preferences</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Used for calendar-synced meetings. Manual "Send bot" actions always record.
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm">Recording mode</Label>
            <div className="space-y-2 rounded-lg border border-border p-3 text-sm">
              {[
                { id: "all", label: "Record all my meetings", desc: "Bot joins every video meeting on your calendar." },
                { id: "external", label: "Only external meetings", desc: "Bot joins only when an attendee is outside your domain." },
                { id: "manual", label: "Ask me before each meeting", desc: "Auto-record off. Skip / Record buttons per event." },
              ].map((opt) => {
                const value = prefs.auto_record_external_only ? "external"
                  : !prefs.auto_record_default ? "manual"
                  : "all";
                return (
                  <label key={opt.id} className="flex cursor-pointer items-start gap-3">
                    <input
                      type="radio"
                      className="mt-1"
                      checked={value === opt.id}
                      onChange={() => setPrefs({
                        ...prefs,
                        auto_record_default: opt.id !== "manual",
                        auto_record_external_only: opt.id === "external",
                      })}
                      data-testid={`record-mode-${opt.id}`}
                    />
                    <div>
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Minimum meeting duration (minutes)</Label>
            <Input
              type="number"
              min={0}
              value={prefs.minimum_meeting_duration_minutes}
              onChange={(e) => setPrefs({ ...prefs, minimum_meeting_duration_minutes: parseInt(e.target.value || "0", 10) })}
              data-testid="min-duration-input"
            />
            <p className="text-xs text-muted-foreground">Shorter meetings are skipped.</p>
          </div>

          <div className="space-y-2">
            <Label>Excluded keywords</Label>
            <Input
              value={excludedKw}
              onChange={(e) => setExcludedKw(e.target.value)}
              placeholder="standup, 1:1, interview"
              data-testid="excluded-keywords-input"
            />
            <p className="text-xs text-muted-foreground">Comma-separated. Case-insensitive substring match.</p>
          </div>

          <div className="space-y-2">
            <Label>Excluded email domains</Label>
            <Input
              value={excludedDom}
              onChange={(e) => setExcludedDom(e.target.value)}
              placeholder="acme.com, internal.example.com"
              data-testid="excluded-domains-input"
            />
            <p className="text-xs text-muted-foreground">Meetings with all attendees in these domains are skipped.</p>
          </div>

          <div className="space-y-2">
            <Label>Bot join buffer (seconds before start)</Label>
            <Input
              type="number"
              min={0}
              max={600}
              value={prefs.bot_join_buffer_seconds}
              onChange={(e) => setPrefs({ ...prefs, bot_join_buffer_seconds: parseInt(e.target.value || "60", 10) })}
              data-testid="join-buffer-input"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label className="text-sm">Email me before bot joins</Label>
              <p className="text-xs text-muted-foreground">Heads-up notification before the bot deploys.</p>
            </div>
            <Switch
              checked={prefs.notify_before_recording}
              onCheckedChange={(v) => setPrefs({ ...prefs, notify_before_recording: v })}
              data-testid="notify-before-toggle"
            />
          </div>

          <Button onClick={savePrefs} disabled={savingPrefs} data-testid="save-calendar-prefs">
            {savingPrefs ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      )}
    </div>
  );
}

function BotTab({ user, refresh }) {
  const [botName, setBotName] = useState(user?.bot_display_name || "Noteology AI");
  const [joinMsg, setJoinMsg] = useState(user?.bot_join_message || "");
  const [defaultName, setDefaultName] = useState(user?.bot_default_name || "Noteology AI");
  const [defaultEmail, setDefaultEmail] = useState(user?.bot_default_email || "");
  const [defaultCompany, setDefaultCompany] = useState(user?.bot_default_company || "");
  const [autoRecord, setAutoRecord] = useState(user?.auto_record_all || false);
  const [skipKW, setSkipKW] = useState((user?.skip_meeting_keywords || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", {
        bot_display_name: botName,
        bot_join_message: joinMsg,
        bot_default_name: defaultName,
        bot_default_email: defaultEmail,
        bot_default_company: defaultCompany,
        auto_record_all: autoRecord,
        skip_meeting_keywords: skipKW.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refresh();
      toast.success("Bot preferences saved");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Bot identity</div>

        <div className="space-y-2">
          <Label>Bot display name</Label>
          <Input value={botName} onChange={(e) => setBotName(e.target.value)} data-testid="bot-name-input" />
          <p className="text-xs text-muted-foreground">Shown to other attendees when the bot joins.</p>
        </div>
        <div className="space-y-2">
          <Label>Join message <span className="text-muted-foreground">(optional)</span></Label>
          <Input value={joinMsg} onChange={(e) => setJoinMsg(e.target.value)} placeholder="Hi! I'm here to take notes." data-testid="bot-message-input" />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Defaults for registration-gated meetings</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Used automatically when Noteology needs to register on your behalf (e.g. Zoom webinars).
          </p>
        </div>

        <div className="space-y-2">
          <Label>Default bot name</Label>
          <Input value={defaultName} onChange={(e) => setDefaultName(e.target.value)} data-testid="bot-default-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Default bot email</Label>
          <Input type="email" value={defaultEmail} onChange={(e) => setDefaultEmail(e.target.value)} placeholder="notetaker@yourcompany.com" data-testid="bot-default-email-input" />
          <p className="text-xs text-muted-foreground">Must be a real address you control — some hosts may email confirmations here.</p>
        </div>
        <div className="space-y-2">
          <Label>Default company</Label>
          <Input value={defaultCompany} onChange={(e) => setDefaultCompany(e.target.value)} data-testid="bot-default-company-input" />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Auto-record</div>
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <Label>Auto-record all meetings</Label>
            <p className="text-xs text-muted-foreground mt-1">Bot joins every meeting on your calendar.</p>
          </div>
          <Switch checked={autoRecord} onCheckedChange={setAutoRecord} data-testid="auto-record-switch" />
        </div>
        <div className="space-y-2">
          <Label>Skip meetings containing these keywords</Label>
          <Input value={skipKW} onChange={(e) => setSkipKW(e.target.value)} placeholder="private, 1:1, interview" data-testid="skip-keywords-input" />
          <p className="text-xs text-muted-foreground">Comma-separated. Case-insensitive.</p>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-bot-settings">{saving ? "Saving…" : "Save"}</Button>
    </div>
  );
}

function NotificationsTab({ user, refresh }) {
  const [summaryReady, setSummaryReady] = useState(user?.notification_summary_ready ?? true);
  const [digest, setDigest] = useState(user?.notification_daily_digest ?? false);
  const [recap, setRecap] = useState(user?.notification_weekly_recap ?? true);

  const save = async () => {
    await api.patch("/profile", {
      notification_summary_ready: summaryReady,
      notification_daily_digest: digest,
      notification_weekly_recap: recap,
    });
    await refresh();
    toast.success("Preferences saved");
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-4 max-w-xl">
      {[
        { label: "Email me when a summary is ready", v: summaryReady, set: setSummaryReady, id: "notif-summary" },
        { label: "Daily digest of action items", v: digest, set: setDigest, id: "notif-digest" },
        { label: "Weekly meeting recap", v: recap, set: setRecap, id: "notif-weekly" },
      ].map((item) => (
        <div key={item.id} className="flex items-center justify-between rounded-lg border border-border p-4">
          <Label>{item.label}</Label>
          <Switch checked={item.v} onCheckedChange={item.set} data-testid={item.id} />
        </div>
      ))}
      <Button onClick={save} data-testid="save-notifications">Save</Button>
    </div>
  );
}

function BillingTab({ user, plans, refresh }) {
  const upgrade = async (planId) => {
    await api.post("/billing/checkout", { plan_id: planId });
    await refresh();
    toast.success(`Switched to ${planId} (mocked)`);
  };

  const pct = user ? Math.min(100, (user.hours_used_this_month / user.plan_hours_limit) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Current plan</div>
        <div className="mt-3 flex items-baseline gap-3">
          <span className="font-display text-4xl font-bold capitalize">{user?.plan}</span>
          <span className="font-mono text-xs text-muted-foreground">{user?.plan_hours_limit >= 9999 ? "UNLIMITED" : `${user?.plan_hours_limit} HOURS/MONTH`}</span>
        </div>
        <div className="mt-6">
          <div className="flex justify-between text-xs font-mono text-muted-foreground mb-2">
            <span>{user?.hours_used_this_month?.toFixed(1)}h used</span>
            <span>{user?.plan_hours_limit}h limit</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {plans.map((p) => (
          <div key={p.id} className={`rounded-2xl border bg-card p-6 ${user?.plan === p.id ? "border-primary" : "border-border"}`}>
            <div className="flex items-baseline justify-between">
              <div className="font-display text-xl font-medium">{p.name}</div>
              <div className="font-mono text-sm text-muted-foreground">
                {p.price_inr === 0 ? "Free" : `₹${p.price_inr.toLocaleString("en-IN")}/mo`}
              </div>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              {p.features.slice(0, 3).map((f, i) => <li key={i}>· {f}</li>)}
            </ul>
            <Button
              variant={user?.plan === p.id ? "outline" : "default"}
              size="sm"
              className="mt-6 w-full"
              disabled={user?.plan === p.id}
              onClick={() => upgrade(p.id)}
              data-testid={`billing-choose-${p.id}`}
            >
              {user?.plan === p.id ? "Current plan" : `Switch to ${p.name}`}
            </Button>
          </div>
        ))}
      </div>
      <p className="font-mono text-xs text-muted-foreground">
        MOCKED — checkout instantly upgrades without payment. Razorpay integration is wired but inactive without API keys.
      </p>
    </div>
  );
}
